/**
 * Sanitize the given text for usage in HTML
 * @param {string} text
 * @return {string} HTML sanitized text
 */
const sanitizeText = text => {
    if(!text){
        return '';
    }
    
    //Our tooltips are expected to contain HTML
    //we want to prevent HTML from being injected however
    //thus sanitize via the DOM api
    const span = document.createElement('span');
    span.innerText = text;
    return span.innerHTML;
};

//TODO: Implement for AB brick/AudienceFilter brick, wait for further usecases
/**
 * Interface for updating a set of endpoints for a given brick based on a given property
 */
class PropertyBasedConnectionRemovalStrategy {
    /**
     * Instantiate a new instance of the given strategy
     * @param {JourneyBrick} brick Brick on which to apply the strategy
     * @param {string} propertyName Property to base connection removal on
     */
    apply(brick, propertyName){
        throw 'Not implemented';
    }
}

/**
 * Create a named permanent connection on a given brick
 * the connectionId will be saved in the config of the given brick
 * @param {JourneyBrick} brick
 * @param {Object} config
 * @param {string} config.connectionIdProperty Name of the given property within the given brick config
 * @param {function(connection: JourneyBrickConnection):undefined} [config.configureConnection]
 */
const withNamedPermanentConnection = (brick, config) => {
    const initializePermanentConnection = () => {
        //Our brick is supposed to always have a port with an id matching the given config property
        let connection = brick.outgoingConnections.find(c => c.id === brick.config[config.connectionIdProperty]);

        if(!connection){
            //Ensure our connectionId does not change after the first set; this to avoid generating unnecessary logs for each save
            connection = brick.addConnection({
                id: brick.config[config.connectionIdProperty]
            });
            
            brick.config[config.connectionIdProperty] = connection.id;
        }

        if(config.configureConnection){
            config.configureConnection(connection);
        }

        //Override our delete handler to ensure a new connection gets initialized post invocation
        const _delete = connection.delete;
        connection.delete = function() {
            const deleteRes = _delete.apply(this, arguments);

            //Since it's simpler for us, allow the id to change upon recreation for now
            initializePermanentConnection();

            return deleteRes;
        }
    }

    initializePermanentConnection();
}

/**
 * Dynamically create connections for each value within an array with the given propertyName
 * @param {JourneyBrick} brick JourneyBuilder brick
 * @param {Object} config Configuration defining how/what to use for dynamic connection creation
 * @param {string} config.brickConfigProperty Name of the array property to base connection creation on
 * @param {function(connection: JourneyBrickConnection, propertyValue: Object):undefined} [config.onConnectionUpdated]
 * @param {function(connection: JourneyBrickConnection):boolean} [config.canDeleteConnection] Function returning a boolean indicating whether the connection may be deleted or not
 * when not present, the given connection will always be deleted
 */
const withPropertyBasedConnections = (brick, config) => {
    const _deregisterConnection = brick.deregisterConnection;
    
    //Proxy to original instance
    const deregisterConnectionImpl = (connection) => _deregisterConnection.call(brick, connection);

    const updateDynamicEndpoints = (brickConfig) => {
        const propertyValues = brickConfig[config.brickConfigProperty] || [];

        //Create a copy of our array to prevent modifying the original while enumerating over it
        [...brick.outgoingConnections].forEach(connection => {
            const connectionIsDeleted = !propertyValues.find(propVal => propVal.connectionId === connection.id);

            //Allow our caller to defined
            if(config.canDeleteConnection && !config.canDeleteConnection(connection)){
                return;
            }
            
            //Calling delete will trigger another updateConditionEndpoints cycle;
            //to prevent this we should never call connection.delete()/brick.deregisterConnection(connection)
            if(connectionIsDeleted){
                deregisterConnectionImpl(connection);

                //Ensure connection is properly removed from self/destination
                if(connection.destinationBrick) connection.destinationBrick.deregisterConnection(connection);
            }
        });

        //Create and link new connections for each condition without a connection
        //this can occur both on altering the configuration
        //or on load, when a brick did not have all endpoints connected on save
        propertyValues.forEach(propertyValue => {
            const currentConnection = brick.connections.find(c => c.id === propertyValue.connectionId);

            //Ensure our connectionId does not change after the first set; this to avoid generating unnecessary logs for each save
            const connection = currentConnection || brick.addConnection({
                id: propertyValue.connectionId
            });
            
            propertyValue.connectionId = connection.id;

            if(config.onConnectionUpdated){
                config.onConnectionUpdated(
                    connection,
                    propertyValue
                );
            }
        });
    };

    //Overwrite with our own handler
    //this to ensure updateDynamicEndpoints gets invoked on
    //connection removal
    brick.deregisterConnection = (connection) => {
        deregisterConnectionImpl(connection);
        updateDynamicEndpoints(brick.config);
    };

    Object.defineProperties(brick, {
        _config: {
            enumerable: false,
            configurable: false,
            writable: true,
            value: brick.config
        },
        config: {
            enumerable: true,
            get(){
                return this._config;
            },
            set(newConfig){
                this._config = newConfig;
                updateDynamicEndpoints(this._config);
            }
        }
    });

    updateDynamicEndpoints(brick.config);
}

/**
 * Ensure the given brick has a single unconnected outgoing port at all times
 * @param {JourneyBrick} brick
 * @param {Object} [config]
 * @param {function(connection: JourneyBrickConnection):boolean} [config.isRegularConnection]
 */
const withUnconnectedRegularConnectionAtAllTimes = (brick, config) => {
    const isRegularConnection = (config && config.isRegularConnection) || (_ => true);

    const ensureUnconnectedOutgoingConnection = () => {
        //If at least a single connection doesn't yet have a destination, skip
        //otherwise call addConnection to ensure at least a single connection being present
        const hasUnconnectedConnection = brick.outgoingConnections.some(c =>
            isRegularConnection(c) &&
            !c.destinationBrick
        );

        if(hasUnconnectedConnection) {
            return;
        }

        brick.addConnection();
    }

    const injectTriggerOnDestinationBrickSet = connection => {
        const destinationBrickPropDef = Object.getOwnPropertyDescriptor(connection, 'destinationBrick');

        Object.defineProperty(connection, 'destinationBrick', {
            ...destinationBrickPropDef,
            set() {
                //Original setter needs to be executed first
                //the original setter is what our brick.outgoingConnections getter depends on
                const retVal = destinationBrickPropDef.set.apply(this, arguments);
                ensureUnconnectedOutgoingConnection();
                return retVal;
            }
        });
    }

    withCustomizedOutgoingConnections(brick, connection => injectTriggerOnDestinationBrickSet(connection));

    //Ensure a single outgoing connection for new bricks 
    ensureUnconnectedOutgoingConnection();
}

/**
 * Initialize the given brick with at least a single outgoing connection
 * @param {JourneyBrick} brick
 */
const withConnection = brick => {
    //Ensure touchpoint bricks have at least a single outgoing connection upon creation for ease of use
    if(brick.outgoingConnections.length === 0){
        brick.addConnection();
    }
}

/**
 * Customize all current outgoing connections and hook into the brick addConnection function to customize all new connections
 * @param {JourneyBrick} brick Journey brick
 * @param {function(connection: JourneyBrickConnection)} customizeConnection Customizer function applied to all existing and new outgoing connections
 */
const withCustomizedOutgoingConnections = (
    brick,
    customizeConnection
) => {
    const _addConnection = brick.addConnection;
    brick.addConnection = function(){
        const connection = _addConnection.apply(this, arguments);
        customizeConnection(connection);
        return connection;
    }

    //Also apply customization to all existing outgoing connections
    brick.outgoingConnections.forEach(connection => customizeConnection(connection));
}

/**
 * Remove the ability to add any but the given whitelisted checkpoints
 * @param {JourneyBrickConnection} connection
 * @param {JourneyBuilder.Checkpoint[]} whitelistedCheckpoints
 */
const withWhitelistedCheckpointTypes = (connection, whitelistedCheckpoints) => {
    const whitelistedCheckpointNames = whitelistedCheckpoints.map(c => c.name);
    
    connection.supportedCheckpoints = connection.supportedCheckpoints.filter(
        c => whitelistedCheckpointNames.includes(c)
    );
}

export {
    sanitizeText,
    
    withConnection,
    withPropertyBasedConnections,
    withNamedPermanentConnection,
    withUnconnectedRegularConnectionAtAllTimes,

    withWhitelistedCheckpointTypes,

    withCustomizedOutgoingConnections
}